import { MAX_LABEL_ID_BASE_LENGTH, SURFACE_SEPARATOR, isSurfaceName, UI_LABEL_REGISTRY } from '@canvas-logic/clp-api-types';

function stripSurface(rawLabelId) {
    const idx = rawLabelId.lastIndexOf(SURFACE_SEPARATOR);
    if (idx < 0)
        return { base: rawLabelId };
    const candidate = rawLabelId.slice(idx + SURFACE_SEPARATOR.length);
    if (!isSurfaceName(candidate))
        return { base: rawLabelId };
    return { base: rawLabelId.slice(0, idx), surface: candidate };
}
function withSurface(labelId, surface) {
    return surface === undefined ? labelId : `${labelId}${SURFACE_SEPARATOR}${surface}`;
}
function isStorableLabelIdLength(rawLabelId) {
    return stripSurface(rawLabelId).base.length <= MAX_LABEL_ID_BASE_LENGTH;
}

function applySurface(scopeCandidates, ctx) {
    if (!ctx.surface)
        return scopeCandidates;
    return [...scopeCandidates.map((c) => withSurface(c, ctx.surface)), ...scopeCandidates];
}
function cascadeKeysForUi(canonical, ctx) {
    const out = [];
    if (ctx.cptKey && ctx.cprodKey)
        out.push(`cpt:${ctx.cptKey}.prod:${ctx.cprodKey}.${canonical}`);
    if (ctx.cptKey)
        out.push(`cpt:${ctx.cptKey}.${canonical}`);
    out.push(canonical);
    return applySurface(out, ctx);
}
function cascadeKeysForEntity(canonical, ctx) {
    return applySurface(scopeCascadeForEntity(canonical, ctx), ctx);
}
function scopeCascadeForEntity(canonical, ctx) {
    const segments = canonical.split('.');
    const first = segments[0];
    const colonIdx = first.indexOf(':');
    const firstMarker = colonIdx >= 0 ? first.slice(0, colonIdx) : first;
    if (firstMarker === 'cpt') {
        return cascadeForCpt(canonical, segments, ctx);
    }
    const cpt = ctx.cptKey;
    const cprod = ctx.cprodKey;
    const cmp = ctx.cmpKey;
    const out = [];
    if (cpt && cprod && cmp)
        out.push(`cpt:${cpt}.prod:${cprod}.cmp:${cmp}.${canonical}`);
    if (cpt && cmp)
        out.push(`cpt:${cpt}.cmp:${cmp}.${canonical}`);
    if (cpt && cprod)
        out.push(`cpt:${cpt}.prod:${cprod}.${canonical}`);
    if (cpt)
        out.push(`cpt:${cpt}.${canonical}`);
    out.push(canonical);
    return out;
}
function cascadeForCpt(canonical, segments, ctx) {
    var _a;
    const first = segments[0];
    const canonicalCpt = first.slice('cpt:'.length);
    const rest = segments.slice(1);
    if ((_a = rest[0]) === null || _a === void 0 ? void 0 : _a.startsWith('prod:')) {
        return [canonical];
    }
    const out = [];
    if (ctx.cprodKey && ctx.cptKey === canonicalCpt && rest.length > 0) {
        out.push(`cpt:${canonicalCpt}.prod:${ctx.cprodKey}.${rest.join('.')}`);
    }
    out.push(canonical);
    return out;
}

function layersFromProjectTranslations(cache) {
    if (!cache)
        return {};
    return { selected: cache.translations, default: cache.fallback };
}

const LAYER_ORDER = ['selected', 'default', 'system'];
function resolveLayered(candidates, layers) {
    for (const layer of LAYER_ORDER) {
        const map = layers[layer];
        if (!map)
            continue;
        for (const key of candidates) {
            const value = map[key];
            if (typeof value === 'string' && value.length > 0)
                return { value, layer, key };
        }
    }
    return undefined;
}
function resolve(candidates, cache) {
    var _a;
    return (_a = resolveLayered(candidates, layersFromProjectTranslations(cache))) === null || _a === void 0 ? void 0 : _a.value;
}
function resolveLabel(blob, labelId, systemValue) {
    var _a, _b;
    return (_b = (_a = resolveLayered([labelId], layersFromProjectTranslations(blob))) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : systemValue;
}

const CUSTOM_KEY_SEGMENT_REGEX = /^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/;
const CUSTOM_PREFIX = 'custom:';
const CUSTOM_KEY_MAX_LENGTH = 200;
function validateCustomKey(key) {
    if (key.length === 0 || key.length > CUSTOM_KEY_MAX_LENGTH)
        return false;
    return key.split('.').every((segment) => CUSTOM_KEY_SEGMENT_REGEX.test(segment));
}
function customLabelId(key) {
    return `${CUSTOM_PREFIX}${key}`;
}
function containsHtml(value) {
    return value.includes('<') || value.includes('>');
}

const PLACEHOLDER_TOKEN = /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g;
const PLACEHOLDER_NAME_REGEX = /^[a-zA-Z][a-zA-Z0-9_]*$/;
function extractPlaceholders(text) {
    const names = new Set();
    let m;
    PLACEHOLDER_TOKEN.lastIndex = 0;
    while ((m = PLACEHOLDER_TOKEN.exec(text)) !== null) {
        names.add(m[1]);
    }
    return [...names].sort();
}
function validatePlaceholderName(name) {
    return PLACEHOLDER_NAME_REGEX.test(name);
}
function placeholdersMatch(text, expected) {
    const found = new Set(extractPlaceholders(text));
    if (found.size !== expected.length)
        return false;
    return expected.every((p) => found.has(p));
}

function interpolate(template, vars) {
    if (!vars)
        return template;
    return template.replace(PLACEHOLDER_TOKEN, (token, name) => Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : token);
}

function createTranslator(options) {
    var _a, _b;
    const baseContext = (_a = options.context) !== null && _a !== void 0 ? _a : {};
    const uiRegistry = (_b = options.uiRegistry) !== null && _b !== void 0 ? _b : UI_LABEL_REGISTRY;
    function customWithSource(key, vars) {
        var _a, _b;
        const id = customLabelId(key);
        const hit = resolveLayered([id], options.layers);
        if (hit)
            return Object.assign(Object.assign({}, hit), { value: interpolate(hit.value, vars) });
        const systemValue = (_b = (_a = options.catalog) === null || _a === void 0 ? void 0 : _a[key]) === null || _b === void 0 ? void 0 : _b.systemValue;
        if (systemValue != null)
            return { value: interpolate(systemValue, vars), layer: 'catalog', key: id };
        return { value: id, layer: 'none', key: id };
    }
    function uiWithSource(key, context) {
        const candidates = cascadeKeysForUi(key, context !== null && context !== void 0 ? context : baseContext);
        const hit = resolveLayered(candidates, options.layers);
        if (hit)
            return hit;
        const registryValue = uiRegistry[key];
        if (typeof registryValue === 'string')
            return { value: registryValue, layer: 'registry', key };
        return { value: key, layer: 'none', key };
    }
    function entityWithSource(canonicalLabelId, context, systemValue) {
        const candidates = cascadeKeysForEntity(canonicalLabelId, context !== null && context !== void 0 ? context : baseContext);
        const hit = resolveLayered(candidates, options.layers);
        if (hit)
            return hit;
        if (systemValue != null)
            return { value: systemValue, layer: 'argument', key: canonicalLabelId };
        return { value: canonicalLabelId, layer: 'none', key: canonicalLabelId };
    }
    return {
        custom: (key, vars) => customWithSource(key, vars).value,
        customWithSource,
        ui: (key, context) => uiWithSource(key, context).value,
        uiWithSource,
        entity: (canonicalLabelId, context, systemValue) => entityWithSource(canonicalLabelId, context, systemValue).value,
        entityWithSource,
    };
}

function createCatalogTranslator(catalog, options = {}) {
    const selected = {};
    if (options.language) {
        for (const [key, def] of Object.entries(catalog)) {
            const value = def.translations[options.language];
            if (value != null && value.length > 0)
                selected[customLabelId(key)] = value;
        }
    }
    return createTranslator({ layers: { selected }, catalog, uiRegistry: options.uiRegistry });
}

class LocalizationCapabilityError extends Error {
    constructor(message) {
        super(message);
        this.name = 'LocalizationCapabilityError';
    }
}
class LocalizationRequestError extends Error {
    constructor(message, status, appErrorCode) {
        super(message);
        this.name = 'LocalizationRequestError';
        this.status = status;
        this.appErrorCode = appErrorCode;
    }
}
const LANGUAGE_UNAVAILABLE_ERROR_CODES = [
    'LANGUAGE_NOT_IN_SUPPORTED_LIST',
    'TRANSLATIONS_LANGUAGE_NOT_IN_SNAPSHOT',
];
function isLanguageUnavailableError(error) {
    return (error instanceof LocalizationRequestError &&
        error.appErrorCode != null &&
        LANGUAGE_UNAVAILABLE_ERROR_CODES.includes(error.appErrorCode));
}

export { CUSTOM_KEY_MAX_LENGTH as C, LocalizationRequestError as L, validatePlaceholderName as a, customLabelId as b, containsHtml as c, CUSTOM_PREFIX as d, extractPlaceholders as e, LANGUAGE_UNAVAILABLE_ERROR_CODES as f, LocalizationCapabilityError as g, cascadeKeysForEntity as h, cascadeKeysForUi as i, createCatalogTranslator as j, createTranslator as k, interpolate as l, isLanguageUnavailableError as m, isStorableLabelIdLength as n, layersFromProjectTranslations as o, placeholdersMatch as p, resolveLabel as q, resolve as r, resolveLayered as s, stripSurface as t, validateCustomKey as v, withSurface as w };
//# sourceMappingURL=clientErrors-COds4E9J.mjs.map
